import { AxiosError } from 'axios';
import { openNotification } from 'components';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateDocumentPayload } from 'services/documents/DocumentRepository';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';
import { setSaveStatus } from '../components/editor/grid/reduxStore/saveStatusSlice';
import { useAppDispatch } from '../components/editor/grid/reduxStore/Store';
import { DocumentSaveStatus } from '../components/editor/shared/models/DocumentSaveStatus';
import { ErrorCode } from '../services/socket/SocketEvents';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import { ProspectResponseDtoTypeEnum, type ContentControllerUpdateMutationRequest } from '@proposify/document-service-api-client';
import type { MigrationPersonId } from '../services/entities/MigrationEntities.ts';
import { normalizeToString, toAssigneeUserId, toCompanyId, toPersonId } from '../services/entities/MigrationEntities.ts';

interface DocumentServiceContentPayload {
  assigneeUserId?: string | null;
  companyId?: string | null;
  personId?: string | null;
}

function buildDocumentServiceContent(payload: UpdateDocumentPayload): ContentControllerUpdateMutationRequest['content'] {
  const isPerson = payload.prospect?.type === ProspectResponseDtoTypeEnum.PERSON;
  const isCompany = payload.prospect?.type === ProspectResponseDtoTypeEnum.COMPANY;

  const content: DocumentServiceContentPayload = {};

  if (payload.assignedUser !== undefined) {
    if (payload.assignedUser === null) {
      content.assigneeUserId = null;
    } else {
      const assigneeId = toAssigneeUserId(payload.assignedUser.id);
      content.assigneeUserId = assigneeId ? normalizeToString(assigneeId) : null;
    }
  }

  if (payload.prospect !== undefined) {
    if (payload.prospect === null) {
      content.companyId = null;
      content.personId = null;
    } else {
      const prospectId = toCompanyId(payload.prospect.id);
      const contactId = payload.prospect.contact?.id ? toPersonId(payload.prospect.contact.id) : null;

      if (isCompany) {
        content.companyId = prospectId ? normalizeToString(prospectId) : null;
        content.personId = contactId ? normalizeToString(contactId) : null;
      } else if (isPerson) {
        content.companyId = null;
        content.personId = prospectId ? normalizeToString(prospectId as unknown as MigrationPersonId) : null;
      } else {
        content.companyId = null;
        content.personId = null;
      }
    }
  }

  return content;
}

export default function useUpdateDocumentMutation() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateDocumentPayload) => {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      if (!payload.contentId) {
        return;
      }

      const content = buildDocumentServiceContent(payload);
      const hasContent = content && Object.keys(content).length > 0;

      if (hasContent) {
        await apiDocumentServiceRepository.patchContent({
          contentId: payload.contentId,
          payload: {
            content: content as ContentControllerUpdateMutationRequest['content'],
          },
        });
      }
    },

    onSuccess: (_, payload) => {
      if (payload.contentId) {
        queryClient.setQueryData(
          QueryKeys.getDocumentMetadataByContentId(ContentType.DOCUMENT, payload.contentId),
          (old: unknown) => old ?? null
        );
      }

      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVED }));
    },

    onError: (error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 423) {
        dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED, errCode: ErrorCode.LOCKED }));
      } else {
        openNotification({
          title: t('error'),
          description: t('document.menu.update_error'),
          type: 'error',
        });
      }
    },
  });
}
